import apiClient from "./client";

export type StripeRequirements = {
  currently_due: string[];
  eventually_due: string[];
  past_due: string[];
  pending_verification: string[];
};

export type StripeAccountStatus = {
  connected: boolean;
  stripe_account_id: string | null;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  disabled_reason: string | null;
  onboarding_state: string | null;
  requirements: StripeRequirements;
};

type LegacyStripeAccountStatus = {
  connected?: boolean;
  accountId?: string | null;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
};

function normalizeLegacyStatus(data: LegacyStripeAccountStatus): StripeAccountStatus {
  const stripeAccountId = data.accountId ?? null;
  return {
    connected: Boolean(data.connected ?? stripeAccountId),
    stripe_account_id: stripeAccountId,
    charges_enabled: Boolean(data.chargesEnabled),
    payouts_enabled: Boolean(data.payoutsEnabled),
    disabled_reason: null,
    onboarding_state: null,
    requirements: {
      currently_due: [],
      eventually_due: [],
      past_due: [],
      pending_verification: [],
    },
  };
}

type StripeStatusRaw = {
  connected?: boolean;
  stripe_account_id?: string | null;
  stripeAccountId?: string | null;
  charges_enabled?: boolean;
  chargesEnabled?: boolean;
  payouts_enabled?: boolean;
  payoutsEnabled?: boolean;
  disabled_reason?: string | null;
  onboarding_state?: string | null;
  onboardingState?: string | null;
  requirements?: StripeRequirements;
  [key: string]: unknown;
};

function normalizeStatus(data: StripeStatusRaw): StripeAccountStatus {
  const accountId = data.stripe_account_id ?? data.stripeAccountId ?? null;
  return {
    connected: Boolean(data.connected ?? accountId),
    stripe_account_id: accountId,
    charges_enabled: Boolean(data.charges_enabled ?? data.chargesEnabled),
    payouts_enabled: Boolean(data.payouts_enabled ?? data.payoutsEnabled),
    disabled_reason: data.disabled_reason ?? null,
    onboarding_state: data.onboarding_state ?? data.onboardingState ?? null,
    requirements: data.requirements ?? {
      currently_due: [],
      eventually_due: [],
      past_due: [],
      pending_verification: [],
    },
  };
}

export type FetchStripeAccountStatusOptions = {
  /** When true, backend calls Stripe API to sync and update DB (use for "Reîmprospătează statusul" only). */
  refresh?: boolean;
};

export async function fetchStripeAccountStatus(
  options?: FetchStripeAccountStatusOptions
): Promise<StripeAccountStatus> {
  try {
    const params = options?.refresh ? { refresh: 1 } : undefined;
    const response = await apiClient.get<StripeStatusRaw>("/stripe/connect/status", { params });
    return normalizeStatus(response.data);
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        const legacyResponse = await apiClient.get<LegacyStripeAccountStatus>("/stripe/connect/account-status");
        return normalizeLegacyStatus(legacyResponse.data);
      }
    }
    throw error;
  }
}


